import { z } from "zod";

/**
 * TruckBuddy → SigmaBuddy maintenance-state feed, version 1 (TRBUD-428).
 *
 * One POST per truck, carrying TruckBuddy's MAINTENANCE fact about it: out of
 * service or serviceable, since when, and which open jobs are on it. It is a
 * fact, not an instruction. Dispatch owns the Sylectus status and decides what
 * Sylectus shows (Eden, 22 October 2026); a receipt proves SigmaBuddy stored
 * the fact, never that Sylectus changed.
 *
 * There is deliberately NO dispatch-axis field here, and no field that could
 * hold one: `maintenance.state` is one of `backedMaintenanceStates`
 * (availability.ts), and `sb-maintenance-state.test.ts` fails if a dispatch
 * value ever becomes expressible. No money, no free text from a person, no
 * people.
 *
 * Contract document and fixture: docs/contracts/sb-maintenance-state-v1/.
 */

export const SB_MAINTENANCE_FEED = "truckbuddy.maintenance-state";
/** Largest body TruckBuddy will send, and SigmaBuddy should accept. */
export const SB_MAINTENANCE_STATE_MAX_BYTES = 32 * 1024;
/** Largest receipt or refusal body TruckBuddy reads back. */
export const SB_MAINTENANCE_RECEIPT_MAX_BYTES = 4096;
export const SB_MAINTENANCE_PATH = "/api/integrations/v1/maintenance-states";

const environments = ["local", "staging", "production"] as const;
const timestamp = z.iso.datetime({ precision: 3 });
const exactText = (max: number) => z.string().min(1).max(max)
  .refine((value) => value === value.trim(), "Surrounding whitespace is not allowed.");
/** A per-truck counter, compared numerically (BigInt / Python int), never as text. */
const decimal = z.string().regex(/^[1-9][0-9]{0,18}$/);

export const sbMaintenanceJobSchema = z.object({
  workOrderId: z.string().uuid(),
  /** The work order number; displayed as `WO-<number>`. */
  number: decimal,
  repairKind: z.enum(["in_house", "vendor"]).nullable(),
  /** The stage's display name. Stage names are configuration and may change. */
  stage: exactText(120),
  stageEnteredAt: timestamp.nullable(),
  /** Whether this job keeps the truck off the road, by TruckBuddy's own rule. */
  holdsTruck: z.boolean(),
  waitingOn: z.enum(["parts", "vendor", "approval"]).nullable(),
}).strict();
export type SbMaintenanceJob = z.infer<typeof sbMaintenanceJobSchema>;

export const sbMaintenanceReleaseBases = ["job_completed", "job_cancelled", "released_by_hand", "unknown"] as const;

/**
 * ONE state per truck (Eden, 30 October 2026), what QuoteBuddy's chip says:
 *   marked_down  out of service, not in a bay and no vendor on site
 *   in_shop      a job on it is In bay, vendor On site, or In progress
 *   repaired     no job still holds it and a job finished during this stop,
 *                but nobody has released it yet
 *   released     serviceable again
 * The first three are the `out_of_service` maintenance state, the last is
 * `serviceable`. None is a dispatch value.
 */
export const sbMaintenancePhases = ["marked_down", "in_shop", "repaired", "released"] as const;
export type SbMaintenancePhase = (typeof sbMaintenancePhases)[number];

/** ONE waiting summary per truck: the OLDEST open wait across its open jobs. */
export const sbMaintenanceWaitingSchema = z.object({
  on: z.enum(["parts", "vendor", "approval"]),
  since: timestamp,
}).strict();

const outOfService = z.object({
  state: z.literal("out_of_service"),
  phase: z.enum(["marked_down", "in_shop", "repaired"]),
  downSince: timestamp,
  /** When the job that made it `repaired` finished. Null in any other phase. */
  repairedAt: timestamp.nullable(),
  waiting: sbMaintenanceWaitingSchema.nullable(),
  lastRelease: z.null(),
  jobs: z.array(sbMaintenanceJobSchema).max(50),
}).strict().refine((fact) => (fact.phase === "repaired") === (fact.repairedAt !== null),
  "repairedAt is set exactly when the phase is repaired.");
const serviceable = z.object({
  state: z.literal("serviceable"),
  phase: z.literal("released"),
  downSince: z.null(),
  repairedAt: z.null(),
  waiting: z.null(),
  lastRelease: z.object({ at: timestamp, basis: z.enum(sbMaintenanceReleaseBases) }).strict().nullable(),
  jobs: z.array(sbMaintenanceJobSchema).max(0),
}).strict();
export const sbMaintenanceFactSchema = z.discriminatedUnion("state", [outOfService, serviceable]);
export type SbMaintenanceFact = z.infer<typeof sbMaintenanceFactSchema>;

/** QuoteBuddy's and SigmaBuddy's unit key: upper case, all whitespace removed. */
export function sbMaintenanceUnitKey(sylectusVehicleId: string | null): string | null {
  const key = (sylectusVehicleId ?? "").toUpperCase().replace(/\s+/g, "");
  return key === "" ? null : key;
}

export const sbMaintenanceAssetSchema = z.object({
  /** TruckBuddy's own id, which orders revisions. Not an SB or Sylectus id; map it, never assume it. */
  id: z.string().uuid(),
  /**
   * THE key to match on (Eden, 30 October 2026): the Sylectus vehicle id, upper
   * case, whitespace removed, as QuoteBuddy's `normalizeSigmaBuddyUnitKey`
   * computes it. QuoteBuddy matches by this and nothing else.
   */
  unitKey: exactText(120).nullable(),
  /** The complete Sylectus vehicle id, prefixes and suffixes kept. */
  sylectusVehicleId: exactText(120).nullable(),
  /** For SigmaBuddy's own fallback match only. A truck with a VIN and no unit key cannot be shown in QuoteBuddy. */
  vin: exactText(40).nullable(),
  /** For display only. Never match on it. */
  unitLabel: exactText(120).nullable(),
}).strict()
  .refine((asset) => asset.sylectusVehicleId !== null || asset.vin !== null,
    "A truck is sent only with a Sylectus vehicle id or a VIN to match it on.")
  .refine((asset) => asset.unitKey === sbMaintenanceUnitKey(asset.sylectusVehicleId),
    "unitKey is the Sylectus vehicle id upper-cased with whitespace removed.");
export type SbMaintenanceAsset = z.infer<typeof sbMaintenanceAssetSchema>;

/** What TruckBuddy stores per revision and hashes: the business content only. */
export const sbMaintenanceContentSchema = z.object({
  asset: sbMaintenanceAssetSchema,
  maintenance: sbMaintenanceFactSchema,
}).strict();
export type SbMaintenanceContent = z.infer<typeof sbMaintenanceContentSchema>;

export function sbMaintenanceEventId(environment: string, assetId: string, revision: string): string {
  return `tb-maint:${environment}:${assetId}:${revision}`;
}

export const sbMaintenanceStateSchema = z.object({
  protocolVersion: z.literal(1),
  feed: z.literal(SB_MAINTENANCE_FEED),
  environment: z.enum(environments),
  /**
   * The delivery id: the same for every retry and heartbeat of one revision,
   * never reused for different content. `tb-maint:<environment>:<asset id>:<revision>`.
   */
  eventId: z.string().max(160),
  /** Rises by one each time the truck's content changes. Keep the highest. */
  revision: decimal,
  /** When TruckBuddy built this revision. A retry or heartbeat keeps it. */
  observedAt: timestamp,
  /**
   * When TruckBuddy last re-read its own records and found this revision still
   * true. The daily heartbeat moves it. Grey a fact out when this is more than
   * 24 hours old: that is "stale", where an old `observedAt` is only "unchanged".
   */
  confirmedAt: timestamp,
  /** When this delivery left TruckBuddy. Every attempt has its own. */
  sentAt: timestamp,
  /** SHA-256 hex of the canonical JSON of `{asset, maintenance}`. The three times are not in it. */
  businessDigest: z.string().regex(/^[a-f0-9]{64}$/),
  asset: sbMaintenanceAssetSchema,
  maintenance: sbMaintenanceFactSchema,
}).strict()
  .refine((value) => value.eventId === sbMaintenanceEventId(value.environment, value.asset.id, value.revision),
    "The event id must be tb-maint:<environment>:<asset id>:<revision>.")
  .refine((value) => value.observedAt <= value.confirmedAt, "A revision is confirmed no earlier than it was built.");
export type SbMaintenanceState = z.infer<typeof sbMaintenanceStateSchema>;

/**
 * SigmaBuddy's answer to a POST it accepted (HTTP 200 or 202).
 *   applied     stored, and now its newest fact for the truck
 *   duplicate   this event id with this digest was already stored
 *   superseded  stored nothing: it already holds a HIGHER revision for the truck.
 *               Normal for a late copy of an older revision. For TruckBuddy's
 *               NEWEST revision it means SigmaBuddy is ahead of TruckBuddy's
 *               counter, which needs a coordinated reset, so TruckBuddy records
 *               it as a refusal (`peer_ahead`) and raises it.
 *   unmatched   stored, but no SB vehicle is mapped to the truck yet; SB shows
 *               it for review rather than guessing a match
 */
export const sbMaintenanceReceiptStates = ["applied", "duplicate", "superseded", "unmatched"] as const;
export const sbMaintenanceReceiptSchema = z.object({
  protocolVersion: z.literal(1),
  eventId: z.string().max(160),
  state: z.enum(sbMaintenanceReceiptStates),
}).strict();
export type SbMaintenanceReceipt = z.infer<typeof sbMaintenanceReceiptSchema>;

/**
 * The refusal codes TruckBuddy records, from SigmaBuddy's `{code, message}`
 * error body (the driver feed's shape). Anything else is stored as the HTTP
 * outcome alone; SigmaBuddy's message is never stored or logged.
 */
export const SB_MAINTENANCE_PEER_REFUSALS = [
  "unauthorized",
  "feed_paused",
  "invalid_payload",
  "event_conflict",
  // Eden, 30 October 2026: a lower revision than SigmaBuddy holds is a 409.
  "stale_revision",
  "payload_too_large",
] as const;
export type SbMaintenancePeerRefusal = (typeof SB_MAINTENANCE_PEER_REFUSALS)[number];

/**
 * The data type both systems name in the connection registry's capability
 * catalog (`api-connections.ts`). TruckBuddy advertises it as one it can send;
 * SigmaBuddy advertises it as one it can receive; an administrator must then
 * approve "send" on the SigmaBuddy connection. Discovery alone grants nothing.
 */
export const SB_MAINTENANCE_CAPABILITY = { id: SB_MAINTENANCE_FEED, schemaVersion: "1" } as const;
/** The registry slug of the connection this feed sends to. */
export const SB_MAINTENANCE_CONNECTION_SLUG = "sigmabuddy";

/** Why the feed cannot send right now, in the order they are checked. */
export const sbMaintenanceBlockers = [
  "switched_off",
  "no_environment",
  "no_connection",
  "connection_paused",
  "no_address",
  "no_credential",
  "no_encryption_key",
  "credential_expired",
  "credential_unreadable",
  "not_discovered",
  "not_permitted",
] as const;
export type SbMaintenanceBlocker = (typeof sbMaintenanceBlockers)[number];

export const sbMaintenanceBlockerMessages: Record<SbMaintenanceBlocker, string> = {
  switched_off: "The feed is switched off.",
  no_environment: "This server has no integration environment set (TB_INTEGRATION_ENVIRONMENT).",
  no_connection: "No SigmaBuddy connection is saved for this environment. Add one under Manage API connections.",
  connection_paused: "The SigmaBuddy connection is paused.",
  no_address: "The SigmaBuddy connection has no site address.",
  no_credential: "The SigmaBuddy connection has no outgoing credential saved.",
  no_encryption_key: "This server cannot read saved credentials (TB_CONNECTION_ENCRYPTION_KEY is not set).",
  credential_expired: "The SigmaBuddy connection's outgoing credential has expired.",
  credential_unreadable: "The saved SigmaBuddy credential cannot be read with this server's key. Enter it again.",
  not_discovered: "SigmaBuddy has not advertised that it can receive maintenance states. Test the connection to discover it.",
  not_permitted: "Sending maintenance states to SigmaBuddy is not approved on the connection's data permissions.",
};

/** What Settings → Connections & bots shows for the feed. No payloads, no credentials. */
export interface MaintenanceFeedStatus {
  enabled: boolean;
  controlVersion: number;
  /** Every reason it cannot send, beyond the switch. Empty when ready. */
  blockers: SbMaintenanceBlocker[];
  baselineAt: string | null;
  lastReconcileAt: string | null;
  lastRun: { at: string; outcome: "ok" | "partial" | "failed" | "skipped"; counts: Record<string, number | string> } | null;
  waitingChanges: number;
  trucks: {
    pending: number; retrying: number; delivered: number; failed: number; refused: number; skipped: number;
  };
  lastDeliveredAt: string | null;
  /** The trucks that need a person: refused or given up. Newest first, at most 20. */
  problems: Array<{
    assetId: string; unitLabel: string | null; revision: string; status: "failed" | "refused";
    errorCode: string | null; httpStatus: number | null; lastAttemptAt: string | null;
  }>;
}
